import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NonNullableFormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject, noop } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import {
  BASE_CUSTOMER_INQUIRY_STATUS_OPTIONS,
  BaseButtonSize,
  BaseCustomerInquiryFilters,
  BaseFilterService,
  BaseInquiryFilters,
  BaseInquiryStatus,
  BaseModalService,
  BaseQueryParamService,
  BaseSearchComponent,
  QualificationShortInfoResponse,
  UserShortInfoResponse,
  CustomerDepartmentFilterInfoResponse,
} from 'base';
import {
  FilterFormCustomerInquiryList,
  BaseCustomerInquiryFilterFormInterface,
} from '../../../_interfaces/base-customer-inquiry-filter-form.interface';

@Component({
  selector: 'base-customer-inquiry-list-filters',
  templateUrl: './base-customer-inquiry-list-filters.component.html',
})
export class BaseCustomerInquiryListFiltersComponent implements OnInit, OnDestroy {
  @Input() qualifications: QualificationShortInfoResponse[] = [];
  @Input() departments: CustomerDepartmentFilterInfoResponse[] = [];
  @Input() createdByCustomers: UserShortInfoResponse[] = [];
  @Input() filterValues: { [key in any]: any };
  @Input() canPublishInquiry: boolean;
  @Output() filtersChanged: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('searchField') searchField: BaseSearchComponent;
  @ViewChild('staffRequest', { static: true }) staffRequest: ElementRef;
  baseCustomerInquiryFilters = BaseCustomerInquiryFilters;
  baseInquiryStatusOption = BASE_CUSTOMER_INQUIRY_STATUS_OPTIONS;
  activeFilters: FilterFormCustomerInquiryList[] = [];
  buttonSizes = BaseButtonSize;
  modalRef: NgbModalRef;
  filtersForm: FormGroup<BaseCustomerInquiryFilterFormInterface> = this.fb.group({
    [BaseInquiryFilters.status]: [''],
    [BaseInquiryFilters.customerDepartments]: [[] as number[]],
    [BaseInquiryFilters.mainQualifications]: [[] as number[]],
    [BaseInquiryFilters.createdBy]: [''],
    [BaseCustomerInquiryFilters.search]: [''],
  });

  private destroy$ = new Subject();

  constructor(
    private fb: NonNullableFormBuilder,
    private readonly modalService: BaseModalService,
    public filterService: BaseFilterService,
    private queryParamsService: BaseQueryParamService,
    public activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.initFilterValuesByQueryParams();

    this.filtersForm.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(50)).subscribe(() => {
      this.activeFilters = this.filterService.updateActiveFilters(this.filtersForm.getRawValue());
      this.queryParamsService.filterSubject.next({
        filters: this.filtersForm.value,
        page: 1,
      });
    });
  }

  removeFilter(filter: FilterFormCustomerInquiryList): void {
    this.filterService.removeFilter(this.filtersForm, filter);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  clearFilters(): void {
    this.filtersForm.reset();
  }

  onSearchChange(query: string): void {
    this.filtersForm.controls[BaseCustomerInquiryFilters.search].setValue(query);
    this.queryParamsService.filterSubject.next({
      page: this.activatedRoute.snapshot.queryParams.page,
      filters: this.filtersForm.value,
    });
  }

  openNewStaffRequestModal() {
    this.modalRef = this.modalService.open(
      {
        headText: 'inquiry.new',
        bodyTemplate: this.staffRequest,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );
    this.modalRef.result.then(noop, noop);
  }

  private initFilterValuesByQueryParams() {
    this.filterValues = this.filterService.setInitialFilterValuesByQueryParams(
      this.filterValues,
      {
        mainQualifications: this.filterService.convertArrayToKeyValueObject(this.qualifications, 'id', 'name'),
        customerDepartments: this.filterService.convertArrayToKeyValueObject(this.departments, 'id', 'name'),
        createdBy: this.filterService.convertArrayToKeyValueObject(this.createdByCustomers, 'id', [
          'firstName',
          'lastName',
        ]),
        status: {
          [BaseInquiryStatus.published]: 'states.published',
          [BaseInquiryStatus.closed]: 'states.closed',
          [BaseInquiryStatus.unpublished]: 'states.unpublished',
        },
      },
      ['search']
    );

    if (!this.filterService.hasFilterValues(this.filterValues)) {
      return;
    }

    this.filtersForm.patchValue(this.filterValues);
    this.activeFilters = this.filterService.updateActiveFilters(this.filtersForm.getRawValue());
  }
}
